// WorkshopAgenda.js
import React from 'react';
import { Container, Row, Col, Card } from 'react-bootstrap';
import Markdown from './Markdown.js';
import { BaseData, List } from './BaseData.js';
import { RenderControlData } from './RenderControlData.js';

// Single timeline entry
const TimelineItem = ({ time, title, description, isLast }) => {
  const itemStyle = { position: 'relative', marginBottom: isLast ? 0 : '2rem' };
  const circleStyle = {
    position: 'absolute',
    left: 0,
    top: 0,
    width: '1rem',
    height: '1rem',
    borderRadius: '50%',
    backgroundColor: '#0d6efd',
  };
  const contentStyle = { marginLeft: '1.5rem' };

  return (
    <div style={itemStyle}>
      <div style={circleStyle} />
      <div style={contentStyle}>
        <strong>{time}</strong>
        <p className="mb-1"><strong>{title}</strong></p>
        <p className="text-muted mb-0">{description}</p>
      </div>
    </div>
  );
};


class TimelineAgendaData extends BaseData {
  static annotations = {
    title: String,
    description: String,
  };
}

class WorkshopSectionData extends RenderControlData {
  static annotations = Object.assign({}, RenderControlData.annotations, {
    preHeading: String,
    heading: String,
    description: String,
    cardTitle: String,
    timelineData: List(TimelineAgendaData),
  });
}



const WorkshopAgenda = ({ section }) => {
  const {
    preHeading,
    heading,       // can be a string or JSX
    description,
    cardTitle,
    timelineData,  // array of { time, title, description }
    spineStyle,    // your vertical line styling
    sectionBg = '#f8f9fa',
    sectionClass = 'py-5',
    cardPaddingLeft = '2.5rem',
  } = section || {};

  return (
  <section className={sectionClass} style={{ backgroundColor: sectionBg }}>
    <Container>
      <Row className="align-items-center">
        {/* Left column */}
        <Col md={6} className="mb-4 mb-md-0">
          <small className="text-uppercase text-muted">
            {preHeading}
          </small>
          <h2 className="mt-2">
           <Markdown md={heading} />
          </h2>
          <p className="mt-3">{description}</p>
        </Col>

        {/* Right column */}
        <Col md={6}>
          <Card className="shadow-sm border-0">
            <Card.Header className="bg-white border-0 pb-0">
              <h5 className="mb-4">{cardTitle}</h5>
            </Card.Header>
            <Card.Body style={{ position: 'relative', paddingLeft: cardPaddingLeft }}>
              <div style={spineStyle} />
              {timelineData &&timelineData.map((item, idx) => (
                <TimelineItem
                  key={idx}
                  time={item.time}
                  title={item.title}
                  description={item.description}
                  isLast={idx === timelineData.length - 1}
                />
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  </section>

  );
};  
export { WorkshopAgenda, TimelineAgendaData, WorkshopSectionData };
export default WorkshopAgenda;

/**
 * Overview (how this component works)
 *
 * WorkshopAgenda is a two-column “agenda + timeline” section intended for landing pages. The left column presents the
 * section’s marketing copy (a small pre-heading label, a main heading rendered through Markdown, and a short description).
 * The right column renders a card containing a vertical timeline: a “spine” line (passed in via spineStyle) and a list of
 * timeline entries. Each entry is rendered by TimelineItem, which places a colored dot on the left and text content on the
 * right. Spacing is handled by marginBottom, with the last item removing bottom margin for a clean finish.
 *
 * The component is purely presentational:
 * - It does not fetch data or manage internal state.
 * - It expects the caller (your Section system) to pass a single object (section) or props that match the fields below.
 * - The only “rich” behavior is that the main heading is Markdown-enabled.
 *
 * Key conventions:
 * - timelineData is an ordered list; rendering order is the schedule order.
 * - spineStyle is injected so you can control the vertical line position/color without hardcoding it inside the component.
 * - sectionBg / sectionClass allow basic layout theming without modifying the component.
 *
 *
 * LLM EXAMPLE JSON (WorkshopAgenda / WorkshopSectionData)
 *
 * {
 *   "render_control_type": "workshop",
 *   "preHeading": "2-day workshop",
 *   "heading": "Creativity and Automation\\nwith Practical AI",
 *   "description": "A hands-on agenda focused on repeatable creative workflows, tools, and applied practice.",
 *   "cardTitle": "Schedule",
 *   "timelineData": [
 *     {
 *       "time": "09:00",
 *       "title": "Welcome + goals",
 *       "description": "Set expectations, define outcomes, and align on what we will build together."
 *     },
 *     {
 *       "time": "10:00",
 *       "title": "Core concepts",
 *       "description": "A guided walkthrough of key concepts, with examples and short exercises."
 *     },
 *     {
 *       "time": "13:00",
 *       "title": "Hands-on lab",
 *       "description": "Build a workflow end-to-end and iterate based on feedback."
 *     },
 *     {
 *       "time": "16:30",
 *       "title": "Wrap-up",
 *       "description": "Review learnings, next steps, and take-home materials."
 *     }
 *   ]
 * }
 *
 * Notes:
 * - "render_control_type" must be "workshop" so the Section registry routes to WorkshopAgenda.
 * - "heading" supports Markdown (it is passed through <Markdown md={heading} />).
 * - "timelineData" items are expected to include: time, title, description (even if your BaseData annotations are minimal).
 * - Optional presentation props (typically provided by the caller, not JSON): spineStyle, sectionBg, sectionClass, cardPaddingLeft.
 */
